import type { InputHTMLAttributes, ReactNode, SyntheticEvent } from 'react';

/** Shared pieces of the sign-in screens (AuthGate, VerifyEmail, ForgotPassword). */

const AUTH_INPUT_CLASS =
  'w-full rounded-lg bg-(--color-bg-tertiary) border border-(--color-border) px-3 py-2.5 text-(--color-text-primary) placeholder-(--color-text-secondary) text-sm focus:outline-none focus:ring-2 focus:ring-(--color-accent)/50';

interface AuthCardProps {
  title: string;
  subtitle: string;
  onSubmit: (e: SyntheticEvent) => void;
  children: ReactNode;
}

export function AuthCard({ title, subtitle, onSubmit, children }: Readonly<AuthCardProps>) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-(--color-bg-primary)">
      <form
        onSubmit={onSubmit}
        noValidate
        className="w-full max-w-sm mx-4 rounded-2xl bg-(--color-bg-secondary) border border-(--color-border) p-8 flex flex-col gap-5 shadow-xl"
      >
        <div className="flex flex-col items-center gap-2 text-center">
          <span className="text-4xl">{'\u{1F6B4}'}</span>
          <h1 className="text-xl font-bold text-(--color-text-primary)">{title}</h1>
          <p className="text-sm text-(--color-text-secondary)">{subtitle}</p>
        </div>
        {children}
      </form>
    </div>
  );
}

/** A text field styled like the sign-in form's. */
export function AuthInput(props: Readonly<InputHTMLAttributes<HTMLInputElement>>) {
  return <input {...props} className={AUTH_INPUT_CLASS} />;
}

interface CodeInputProps {
  value: string;
  onChange: (value: string) => void;
  autoFocus?: boolean;
  label?: string;
}

/** The 6-digit email code: numeric keypad and one-time-code autofill. */
export function CodeInput({ value, onChange, autoFocus, label = '000000' }: Readonly<CodeInputProps>) {
  return (
    <input
      type="text"
      inputMode="numeric"
      autoComplete="one-time-code"
      pattern="[0-9]{6}"
      maxLength={6}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={label}
      aria-label={label}
      autoFocus={autoFocus}
      className={`${AUTH_INPUT_CLASS} text-center text-2xl tracking-[0.5em] font-mono`}
    />
  );
}

interface PrimaryButtonProps {
  disabled?: boolean;
  loading?: boolean;
  children: ReactNode;
}

export function PrimaryButton({ disabled, loading, children }: Readonly<PrimaryButtonProps>) {
  return (
    <button
      type="submit"
      disabled={disabled}
      className="w-full py-2.5 rounded-lg font-semibold text-sm bg-green-400 text-gray-900 hover:bg-green-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
    >
      {loading && <Spinner />}
      {children}
    </button>
  );
}

export function Spinner() {
  return (
    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
    </svg>
  );
}
